/**
 * /review-budget — PUBLIC route (CONSTRUCTION-BUDGET-APPROVAL-001, Inc 3).
 *
 * The page a budget approver's emailed link opens. Reads ?token= from the URL,
 * fetches the budget via GET /api/budget-review/{token} (token-authorized, no
 * login), and lets the approver read the budget, comment, and approve / request
 * changes. External approvers never sign in.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { api, errorMessage } from "@/lib/api";

export const Route = createFileRoute("/review-budget")({
  component: ReviewBudgetPage,
});

interface ReviewLine {
  phase: string | null;
  cost_code: string | null;
  cost_type: string;
  amount: string;
  hours: string | null;
}
interface ReviewApprover {
  tier_index: number; name: string; role: string; external: boolean; status: string;
}
interface ReviewData {
  project_name: string;
  project_code: string;
  budget_total: string;
  status: string;
  your_name: string;
  your_step: number;
  step_count: number;
  is_your_turn: boolean;
  already_acted: boolean;
  lines: ReviewLine[];
  approvers: ReviewApprover[];
}

const money = (v: string | number) =>
  `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function ReviewBudgetPage() {
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const [data, setData] = useState<ReviewData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [posted, setPosted] = useState<string | null>(null);
  const [done, setDone] = useState<"approve" | "request_changes" | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [chatMsgs, setChatMsgs] = useState<{ role: "user" | "assistant"; content: string }[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [xlsxMsg, setXlsxMsg] = useState<string | null>(null);
  const [xlsxBusy, setXlsxBusy] = useState(false);

  const load = () =>
    api<ReviewData>(`/api/budget-review/${token}`)
      .then(setData)
      .catch(() => setLoadError("This review link is invalid, already used, or expired."));

  useEffect(() => {
    if (!token) { setLoadError("Missing review link token."); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function postComment() {
    if (!comment.trim()) return;
    setBusy(true); setActionError(null);
    try {
      await api(`/api/budget-review/${token}/comment`, { method: "POST", body: { text: comment.trim() } });
      setPosted("Comment sent to the team.");
      setComment("");
    } catch (e) { setActionError(errorMessage(e)); }
    finally { setBusy(false); }
  }

  async function act(action: "approve" | "request_changes") {
    setBusy(true); setActionError(null);
    try {
      await api(`/api/budget-review/${token}/action`, {
        method: "POST", body: { action, note: note.trim() || null },
      });
      setDone(action);
    } catch (e) { setActionError(errorMessage(e)); }
    finally { setBusy(false); }
  }

  async function sendChat() {
    const q = chatInput.trim();
    if (!q) return;
    const next = [...chatMsgs, { role: "user" as const, content: q }];
    setChatMsgs(next); setChatInput(""); setChatBusy(true);
    try {
      const r = await api<{ answer: string }>(`/api/budget-review/${token}/chat`, {
        method: "POST", body: { message: q, history: chatMsgs },
      });
      setChatMsgs([...next, { role: "assistant" as const, content: r.answer }]);
    } catch {
      setChatMsgs([...next, { role: "assistant" as const, content: "Sorry — I couldn't answer that just now." }]);
    } finally { setChatBusy(false); }
  }

  async function uploadXlsx(f: File) {
    setXlsxBusy(true); setXlsxMsg(null);
    try {
      const fd = new FormData(); fd.append("file", f);
      const res = await fetch(`/api/budget-review/${token}/excel`, { method: "POST", body: fd });
      if (!res.ok) throw new Error();
      const j = await res.json();
      setXlsxMsg(j.drafted > 0
        ? `${j.drafted} change${j.drafted > 1 ? "s" : ""} sent to the team for review.`
        : "Thanks — no amount changes were detected in that workbook.");
    } catch {
      setXlsxMsg("Couldn't read that file — please upload the .xlsx you downloaded.");
    } finally { setXlsxBusy(false); }
  }

  if (loadError) {
    return (
      <Centered>
        <Paper variant="outlined" sx={{ p: 4, maxWidth: 460, textAlign: "center" }}>
          <Typography variant="h6" gutterBottom>Link unavailable</Typography>
          <Typography variant="body2" color="text.secondary">{loadError}</Typography>
        </Paper>
      </Centered>
    );
  }
  if (!data) {
    return <Centered><CircularProgress /></Centered>;
  }

  if (done) {
    return (
      <Centered>
        <Paper variant="outlined" sx={{ p: 4, maxWidth: 480, textAlign: "center" }}>
          <CheckCircleOutlineIcon color="success" sx={{ fontSize: 44 }} />
          <Typography variant="h6" sx={{ mt: 1 }}>
            {done === "approve" ? "Budget approved — thank you" : "Changes requested"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {done === "approve"
              ? `Your approval of the ${data.project_name} budget is recorded. If there are more approvers, it moves to the next one.`
              : `Your feedback is recorded and the budget goes back to the ${data.project_code} team to revise.`}
          </Typography>
        </Paper>
      </Centered>
    );
  }

  const canAct = data.is_your_turn && !data.already_acted && data.status === "pending";

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", px: 2, py: 4 }}>
      {/* Banner */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 2, display: "flex", gap: 2, alignItems: "center" }}>
        <Box sx={{ fontSize: 34, lineHeight: 1 }}>📋</Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6">Review a job budget</Typography>
          <Typography variant="body2" color="text.secondary">
            Budget for <b>{data.project_name}</b> ({data.project_code}) —{" "}
            <b>{money(data.budget_total)}</b>
          </Typography>
        </Box>
        {data.status !== "pending" ? (
          <Chip label={data.status === "approved" ? "Approved" : data.status} />
        ) : canAct ? (
          <Chip color="warning" variant="outlined"
            label={`Your turn · Step ${data.your_step} of ${data.step_count}`} sx={{ fontWeight: 600 }} />
        ) : (
          <Chip variant="outlined" label={`Step ${data.your_step} of ${data.step_count} · waiting`} />
        )}
      </Paper>

      {!canAct && data.status === "pending" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {data.already_acted
            ? "You've already acted on this budget — it's shown here for your records."
            : "It isn't your turn yet, or this link has been superseded. You can review the budget below."}
        </Alert>
      )}

      {/* Budget table */}
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
          <Typography variant="subtitle2">Budget detail · {data.lines.length} lines</Typography>
        </Box>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Phase</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Cost code</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Budget</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Hrs</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.lines.map((ln, i) => (
                <TableRow key={i} hover>
                  <TableCell>{ln.phase ?? "—"}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{ln.cost_code ?? "—"}</TableCell>
                  <TableCell sx={{ textTransform: "capitalize" }}>{ln.cost_type}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{money(ln.amount)}</TableCell>
                  <TableCell align="right" sx={{ color: "text.secondary" }}>
                    {ln.hours ? Number(ln.hours).toLocaleString() : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <Box sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: "divider", display: "flex", justifyContent: "space-between" }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>Revised total</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
            {money(data.budget_total)}
          </Typography>
        </Box>
      </Paper>

      {/* Work in Excel */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2">Prefer to work in Excel?</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Download the budget, mark up the amounts and add comments, then send it back — we diff it and route your changes to the team.
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <Button variant="outlined" onClick={() => window.open(`/api/budget-review/${token}/excel`, "_blank")}>
            ↓ Download budget (.xlsx)
          </Button>
          <Button variant="outlined" component="label" disabled={xlsxBusy}>
            ↑ Upload revised
            <input hidden type="file" accept=".xlsx"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadXlsx(f); e.target.value = ""; }} />
          </Button>
          {xlsxBusy && <CircularProgress size={16} />}
          {xlsxMsg && <Typography variant="caption" color="success.main">{xlsxMsg}</Typography>}
        </Stack>
      </Paper>

      {/* Ask the assistant */}
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Box sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: "divider" }}>
          <Typography variant="subtitle2">Ask the budget assistant</Typography>
          <Typography variant="caption" color="text.secondary">
            Read-only · answers only about this budget
          </Typography>
        </Box>
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
          {chatMsgs.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              e.g. “What's the biggest line?” · “How much of this is labor?” · “How does it compare to the bid?”
            </Typography>
          )}
          {chatMsgs.map((m, i) => (
            <Box key={i} sx={{
              alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "88%",
              px: 1.5, py: 1, borderRadius: 2, fontSize: 13, whiteSpace: "pre-wrap",
              bgcolor: m.role === "user" ? "action.hover" : "warning.light",
              color: m.role === "user" ? "text.primary" : "warning.contrastText",
            }}>{m.content}</Box>
          ))}
          {chatBusy && <CircularProgress size={16} sx={{ alignSelf: "flex-start" }} />}
          <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
            <TextField fullWidth size="small" value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
              placeholder="Ask about any line, the total, or the mix…" />
            <Button variant="contained" disabled={chatBusy || !chatInput.trim()} onClick={sendChat}>Send</Button>
          </Box>
        </Box>
      </Paper>

      {/* Comment */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Leave a comment for the team</Typography>
        <TextField fullWidth multiline minRows={2} size="small" value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="e.g. Concrete looks high for this market — can you share the buyout backup?" />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
          <Typography variant="caption" color="success.main">{posted}</Typography>
          <Button size="small" startIcon={<EditOutlinedIcon />} disabled={busy || !comment.trim()}
            onClick={postComment}>Post comment</Button>
        </Box>
      </Paper>

      {/* Decision */}
      {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Your decision</Typography>
        <TextField fullWidth size="small" value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note recorded with your decision" sx={{ mb: 1.5 }} />
        <Stack direction="row" spacing={1.5}>
          <Button variant="contained" color="success" disabled={!canAct || busy}
            startIcon={<CheckCircleOutlineIcon />} onClick={() => act("approve")}>
            Approve budget
          </Button>
          <Button variant="outlined" color="error" disabled={!canAct || busy}
            onClick={() => act("request_changes")}>
            Request changes
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
          🔒 Secure single-use link · nothing you do here posts to anyone's books — a budget is a plan.
        </Typography>
      </Paper>
    </Box>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 2 }}>
      {children}
    </Box>
  );
}
